import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Eye, EyeOff, Loader2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, CardHeader, Input } from '@/components/ui';
import { ontologyApi, type OntologyClassSummary } from '@/lib/api';
import { cn } from '@/lib/cn';

/**
 * Read-only browser of the live ontology — class tree on the left, detail
 * panel on the right. Drafts and gap-driven additions still flow through
 * `OntologyDraftEditor`; this surface answers "what's in the ontology right
 * now?" without making the analyst dig into Turtle.
 */
export function OntologyBrowser() {
  const { t } = useTranslation(['studio', 'common']);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const { data: classes, isLoading } = useQuery({
    queryKey: ['studio', 'ontology', 'classes'],
    queryFn: () => ontologyApi.listClasses(undefined, 5000),
    staleTime: 5 * 60_000,
  });

  const { data: overrides } = useQuery({
    queryKey: ['studio', 'ontology', 'overrides'],
    queryFn: () => ontologyApi.listOverrides(),
    staleTime: 30_000,
  });

  // Index disabled IRIs by kind so the tree + detail panel can render
  // their state without re-iterating the override list on every row.
  const disabled = useMemo(() => {
    const out = { property: new Set<string>(), class: new Set<string>() };
    for (const it of overrides?.items ?? []) out[it.kind].add(it.iri);
    return out;
  }, [overrides]);

  // Filter client-side so toggling search doesn't re-hit the server.
  const filtered = useMemo(() => {
    if (!classes) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return classes;
    return classes.filter(
      (c) =>
        c.local_name.toLowerCase().includes(needle) ||
        (c.label?.toLowerCase().includes(needle) ?? false),
    );
  }, [classes, search]);

  return (
    <div className="grid h-[calc(100vh-3.5rem)] grid-cols-[35%_65%] gap-3 p-3">
      <Card className="flex flex-col overflow-hidden">
        <CardHeader>
          <h2 className="text-h2 text-brand-900">{t('studio:browser.title')}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-small text-slate-600">
            {classes
              ? t('studio:browser.classCount', { count: classes.length })
              : t('common:status.loading')}
            {overrides && (overrides.counts.class > 0 || overrides.counts.property > 0) && (
              <span
                className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-900"
                title={t(
                  'studio:browser.disabledHint',
                  'Disabled items are skipped by RDF-01 to lower extraction cost',
                )}
              >
                {t('studio:browser.disabledCount', {
                  classes: overrides.counts.class,
                  properties: overrides.counts.property,
                  defaultValue: 'Disabled: {{classes}} class, {{properties}} property',
                })}
              </span>
            )}
          </div>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" aria-hidden />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('studio:browser.searchPlaceholder')}
              className="pl-8"
            />
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto p-0">
          {isLoading && (
            <div className="flex items-center gap-2 p-4 text-small text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('common:status.loading')}
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="p-4 text-small text-slate-500">{t('common:status.noResults')}</p>
          )}
          {!isLoading && classes && (
            <ClassTree
              search={search}
              all={classes}
              visible={filtered}
              selected={selected}
              onSelect={setSelected}
              disabledClasses={disabled.class}
            />
          )}
        </CardContent>
      </Card>

      <ClassDetailPanel
        iri={selected}
        disabledClasses={disabled.class}
        disabledProperties={disabled.property}
      />
    </div>
  );
}

// ─── Class tree ──────────────────────────────────────────────────

function ClassTree({
  search,
  all,
  visible,
  selected,
  onSelect,
  disabledClasses,
}: {
  search: string;
  all: OntologyClassSummary[];
  visible: OntologyClassSummary[];
  selected: string | null;
  onSelect: (iri: string) => void;
  disabledClasses: Set<string>;
}) {
  // When searching, render the filtered list flat (no parent context needed).
  if (search.trim()) {
    return (
      <ul className="divide-y divide-slate-100">
        {visible.map((c) => (
          <ClassRow
            key={c.iri}
            cls={c}
            depth={0}
            isOpen={false}
            isSelected={c.iri === selected}
            onSelect={() => onSelect(c.iri)}
            onToggle={() => {}}
            showChildrenIndicator={false}
            isDisabled={disabledClasses.has(c.iri)}
          />
        ))}
      </ul>
    );
  }

  // Without search: build a real hierarchy from `parents` and walk it.
  return (
    <Hierarchy
      all={all}
      selected={selected}
      onSelect={onSelect}
      disabledClasses={disabledClasses}
    />
  );
}

function Hierarchy({
  all,
  selected,
  onSelect,
  disabledClasses,
}: {
  all: OntologyClassSummary[];
  selected: string | null;
  onSelect: (iri: string) => void;
  disabledClasses: Set<string>;
}) {
  const byIri = useMemo(() => {
    const m = new Map<string, OntologyClassSummary>();
    for (const c of all) m.set(c.iri, c);
    return m;
  }, [all]);

  const childrenOf = useMemo(() => {
    const m = new Map<string, OntologyClassSummary[]>();
    for (const c of all) {
      for (const p of c.parents) {
        const bucket = m.get(p);
        if (bucket) bucket.push(c);
        else m.set(p, [c]);
      }
    }
    for (const bucket of m.values()) bucket.sort((a, b) => a.local_name.localeCompare(b.local_name));
    return m;
  }, [all]);

  const roots = useMemo(
    () =>
      all
        .filter((c) => c.parents.length === 0 || c.parents.every((p) => !byIri.has(p)))
        .sort((a, b) => a.local_name.localeCompare(b.local_name)),
    [all, byIri],
  );

  return (
    <Branch
      roots={roots}
      childrenOf={childrenOf}
      depth={0}
      selected={selected}
      onSelect={onSelect}
      disabledClasses={disabledClasses}
    />
  );
}

function Branch({
  roots,
  childrenOf,
  depth,
  selected,
  onSelect,
  disabledClasses,
}: {
  roots: OntologyClassSummary[];
  childrenOf: Map<string, OntologyClassSummary[]>;
  depth: number;
  selected: string | null;
  onSelect: (iri: string) => void;
  disabledClasses: Set<string>;
}) {
  return (
    <ul className="divide-y divide-slate-100">
      {roots.map((c) => (
        <TreeNode
          key={c.iri}
          cls={c}
          childrenOf={childrenOf}
          depth={depth}
          selected={selected}
          onSelect={onSelect}
          disabledClasses={disabledClasses}
        />
      ))}
    </ul>
  );
}

function TreeNode({
  cls,
  childrenOf,
  depth,
  selected,
  onSelect,
  disabledClasses,
}: {
  cls: OntologyClassSummary;
  childrenOf: Map<string, OntologyClassSummary[]>;
  depth: number;
  selected: string | null;
  onSelect: (iri: string) => void;
  disabledClasses: Set<string>;
}) {
  const [open, setOpen] = useState(depth < 1);
  const kids = childrenOf.get(cls.iri) ?? [];
  const hasKids = kids.length > 0;
  return (
    <li>
      <ClassRow
        cls={cls}
        depth={depth}
        isOpen={open}
        isSelected={cls.iri === selected}
        onSelect={() => onSelect(cls.iri)}
        onToggle={() => setOpen((v) => !v)}
        showChildrenIndicator={hasKids}
        isDisabled={disabledClasses.has(cls.iri)}
      />
      {hasKids && open && (
        <Branch
          roots={kids}
          childrenOf={childrenOf}
          depth={depth + 1}
          selected={selected}
          onSelect={onSelect}
          disabledClasses={disabledClasses}
        />
      )}
    </li>
  );
}

function ClassRow({
  cls,
  depth,
  isOpen,
  isSelected,
  onSelect,
  onToggle,
  showChildrenIndicator,
  isDisabled = false,
}: {
  cls: OntologyClassSummary;
  depth: number;
  isOpen: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  showChildrenIndicator: boolean;
  isDisabled?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 px-2 py-1.5 text-small hover:bg-slate-50',
        isSelected && 'bg-brand-50 text-brand-900',
      )}
      style={{ paddingLeft: 8 + depth * 14 }}
    >
      {showChildrenIndicator ? (
        <button
          type="button"
          onClick={onToggle}
          className="text-slate-500 hover:text-slate-700"
          aria-label={isOpen ? 'collapse' : 'expand'}
        >
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      ) : (
        <span className="inline-block w-3.5" aria-hidden />
      )}
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'flex-1 truncate text-left font-mono',
          isDisabled && 'text-slate-400 line-through',
        )}
        title={isDisabled ? `${cls.iri} (disabled)` : cls.iri}
      >
        {cls.local_name}
      </button>
      {isDisabled && (
        <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
          off
        </span>
      )}
      {cls.children_count > 0 && (
        <span className="rounded bg-slate-100 px-1.5 text-small text-slate-500">
          {cls.children_count}
        </span>
      )}
    </div>
  );
}

// ─── Detail panel ────────────────────────────────────────────────

function ClassDetailPanel({
  iri,
  disabledClasses,
  disabledProperties,
}: {
  iri: string | null;
  disabledClasses: Set<string>;
  disabledProperties: Set<string>;
}) {
  const { t } = useTranslation(['studio', 'common']);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'ontology', 'class', iri],
    queryFn: () => ontologyApi.classDetail(iri as string),
    enabled: !!iri,
    staleTime: 5 * 60_000,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['studio', 'ontology', 'overrides'] });
  };
  const disable = useMutation({
    mutationFn: (payload: { iri: string; kind: 'property' | 'class' }) =>
      ontologyApi.disableOverride(payload),
    onSuccess: invalidate,
  });
  const enable = useMutation({
    mutationFn: (iri: string) => ontologyApi.enableOverride(iri),
    onSuccess: invalidate,
  });

  if (!iri) {
    return (
      <Card>
        <CardContent className="flex h-full min-h-[20rem] items-center justify-center text-small text-slate-500">
          {t('studio:browser.selectClass')}
        </CardContent>
      </Card>
    );
  }
  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="flex h-full min-h-[20rem] items-center justify-center text-small text-slate-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          {t('common:status.loading')}
        </CardContent>
      </Card>
    );
  }

  const classDisabled = disabledClasses.has(data.iri);
  const toggleClass = () =>
    classDisabled
      ? enable.mutate(data.iri)
      : disable.mutate({ iri: data.iri, kind: 'class' });

  return (
    <Card className="overflow-auto">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2
                className={cn(
                  'text-h2 text-brand-900',
                  classDisabled && 'text-slate-400 line-through',
                )}
              >
                {data.local_name}
              </h2>
              {classDisabled && (
                <Badge variant="warning">
                  {t('studio:browser.disabledBadge', 'disabled')}
                </Badge>
              )}
            </div>
            <p className="break-all font-mono text-small text-slate-500">{data.iri}</p>
            {data.label && data.label !== data.local_name && (
              <p className="mt-1 text-body text-slate-700">{data.label}</p>
            )}
          </div>
          <Button
            variant={classDisabled ? 'secondary' : 'ghost'}
            size="sm"
            onClick={toggleClass}
            disabled={disable.isPending || enable.isPending}
            title={
              classDisabled
                ? t('studio:browser.enableClass', 'Re-enable this class')
                : t(
                    'studio:browser.disableClass',
                    'Disable — RDF-01 will stop emitting instances of this class',
                  )
            }
          >
            {classDisabled ? (
              <>
                <Eye className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t('studio:browser.enable', 'Enable')}
              </>
            ) : (
              <>
                <EyeOff className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t('studio:browser.disable', 'Disable')}
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-small">
        {data.comment && (
          <Section title={t('studio:browser.sections.comment')}>
            <p className="text-slate-700">{data.comment}</p>
          </Section>
        )}
        <Section title={t('studio:browser.sections.parents')}>
          <IriList iris={data.parents} empty={t('studio:browser.noParents')} />
        </Section>
        <Section title={t('studio:browser.sections.children', { count: data.children.length })}>
          <IriList iris={data.children} empty={t('studio:browser.noChildren')} />
        </Section>
        <Section title={t('studio:browser.sections.domain')}>
          <PropertyIriList
            iris={data.used_as_domain}
            empty={t('common:status.empty')}
            disabledProperties={disabledProperties}
            onToggle={(propIri, isOff) =>
              isOff
                ? enable.mutate(propIri)
                : disable.mutate({ iri: propIri, kind: 'property' })
            }
            busy={disable.isPending || enable.isPending}
          />
        </Section>
        <Section title={t('studio:browser.sections.range')}>
          <PropertyIriList
            iris={data.used_as_range}
            empty={t('common:status.empty')}
            disabledProperties={disabledProperties}
            onToggle={(propIri, isOff) =>
              isOff
                ? enable.mutate(propIri)
                : disable.mutate({ iri: propIri, kind: 'property' })
            }
            busy={disable.isPending || enable.isPending}
          />
        </Section>
      </CardContent>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-h4 text-slate-700">{title}</p>
      {children}
    </div>
  );
}

function IriList({ iris, empty }: { iris: string[]; empty: string }) {
  if (iris.length === 0) return <p className="text-slate-500">{empty}</p>;
  return (
    <ul className="space-y-0.5">
      {iris.map((iri) => (
        <li key={iri} className="break-all font-mono text-slate-700">
          {localName(iri)}
        </li>
      ))}
    </ul>
  );
}

function PropertyIriList({
  iris,
  empty,
  disabledProperties,
  onToggle,
  busy,
}: {
  iris: string[];
  empty: string;
  disabledProperties: Set<string>;
  onToggle: (iri: string, currentlyDisabled: boolean) => void;
  busy: boolean;
}) {
  if (iris.length === 0) return <p className="text-slate-500">{empty}</p>;
  return (
    <ul className="space-y-0.5">
      {iris.map((iri) => {
        const off = disabledProperties.has(iri);
        return (
          <li key={iri} className="flex items-center gap-2 break-all">
            <span
              className={cn(
                'flex-1 font-mono text-slate-700',
                off && 'text-slate-400 line-through',
              )}
              title={off ? `${iri} (disabled)` : iri}
            >
              {localName(iri)}
            </span>
            {off && (
              <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                off
              </span>
            )}
            <button
              type="button"
              onClick={() => onToggle(iri, off)}
              disabled={busy}
              className="text-slate-400 hover:text-accent disabled:opacity-50"
              aria-label={off ? 'Re-enable property' : 'Disable property'}
              title={off ? 'Re-enable property' : 'Disable — RDF-01 will skip this property'}
            >
              {off ? (
                <Eye className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <EyeOff className="h-3.5 w-3.5" aria-hidden />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function localName(iri: string): string {
  for (const sep of ['#', '/']) {
    if (iri.includes(sep)) return iri.split(sep).pop() || iri;
  }
  return iri;
}
